import {
  FIXED_INCENTIVE,
  PRICE_BASED_INCENTIVE,
  INCENTIVE_PAID_TO_INSTALLER,
} from 'resources/incentives/IncentiveConstants'
import { AdderLibraryRecord, mapAdderLibraryRecordToQuoteRow } from '../lineItems/adderLibraryUtils'
import {
  IncentiveLibraryRecord,
  mapIncentiveLibraryRecordToQuoteRow,
} from '../lineItems/incentiveLibraryUtils'
import { QuoteAdderRow, QuoteIncentiveRow } from '../lineItems/types'
import { QuoteLineItemsPayload } from './quoteDesignSnapshot'

/** Read adders + incentives from a live Studio ``OsSystem`` (or stub with the same keys). */
export const readQuoteLineItemsFromStudioSystem = (studioSystem: any): QuoteLineItemsPayload => {
  if (!studioSystem) {
    return { line_items: [], incentives: [] }
  }

  const line_items: QuoteAdderRow[] = []
  const rawLineItems = studioSystem.line_items ?? studioSystem.userData?.line_items
  if (Array.isArray(rawLineItems)) {
    for (const row of rawLineItems) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const label = typeof r.label === 'string' ? r.label.trim() : ''
      const adderId = r.adder_id
      const hasLibraryRef = typeof adderId === 'number' && adderId > 0
      if (!label && !hasLibraryRef) continue
      line_items.push(row as QuoteAdderRow)
    }
  }

  const incentives: QuoteIncentiveRow[] = []

  const libraryIds: number[] = Array.isArray(studioSystem.incentives)
    ? studioSystem.incentives
    : Array.isArray(studioSystem.userData?.incentives)
      ? studioSystem.userData.incentives
      : []

  for (const id of libraryIds) {
    if (typeof id !== 'number' || id <= 0) continue
    incentives.push({
      incentive_id: id,
      incentive_type: FIXED_INCENTIVE,
      title: '',
      inc_tax: null,
      settings: { value: null, price_based_incentive_type: 'price_including_tax' },
    })
  }

  const rawInstaller =
    studioSystem.incentive_to_installer ?? studioSystem.userData?.incentive_to_installer
  if (Array.isArray(rawInstaller)) {
    for (const row of rawInstaller) {
      if (!row || typeof row !== 'object') continue
      const r = row as Record<string, unknown>
      const title = typeof r.title === 'string' ? r.title.trim() : ''
      const settingsValue =
        typeof (r.settings as { value?: number } | undefined)?.value === 'number'
          ? (r.settings as { value: number }).value
          : typeof r.value === 'number'
            ? r.value
            : null
      if (!title && settingsValue == null) continue
      incentives.push({
        incentive_id: null,
        incentive_type: (r.incentive_type as number) ?? FIXED_INCENTIVE,
        title,
        inc_tax: typeof r.inc_tax === 'number' ? r.inc_tax : settingsValue,
        settings: {
          value: settingsValue,
          price_based_incentive_type:
            (r.settings as { price_based_incentive_type?: QuoteIncentiveRow['settings']['price_based_incentive_type'] })
              ?.price_based_incentive_type ?? 'price_including_tax',
        },
      })
    }
  }

  return { line_items, incentives }
}

/** Whether picker-sourced library adder rows need title / non-solar normalisation. */
export const quoteAdderRowsNeedLibraryHydration = (rows: QuoteAdderRow[]): boolean =>
  rows.some((row) => {
    const id = row.adder_id
    if (typeof id !== 'number' || id <= 0) return false
    return !(row.label ?? '').trim() || row.value_includes_tax !== true
  })

/**
 * The Design "Adder from library" picker writes raw ``/adders/`` records onto the
 * Studio system via ``Adder.addToSystem``, which drops the title (the API field is
 * ``title``, not ``label``) and keeps the library's raw ``formula`` /
 * ``value_includes_tax``. Non-solar quotes expect fixed-price, tax-inclusive line
 * items, so re-map any freshly-picked library row through
 * ``mapAdderLibraryRecordToQuoteRow`` — the same normalisation the inline
 * autocomplete uses — to restore the label and the tax-inclusive fixed formula.
 * Rows that already carry a label and the tax-inclusive flag are left untouched so
 * inline edits to value / quantity are preserved.
 */
export const hydrateLibraryAdderRows = (
  rows: QuoteAdderRow[],
  libraryRecords: AdderLibraryRecord[],
  defaultTaxRate: number
): QuoteAdderRow[] => {
  if (!quoteAdderRowsNeedLibraryHydration(rows)) return rows
  const byId = new Map(libraryRecords.map((record) => [record.id, record]))
  return rows.map((row) => {
    const id = row.adder_id
    if (typeof id !== 'number' || id <= 0) return row
    const needsHydration = !(row.label ?? '').trim() || row.value_includes_tax !== true
    if (!needsHydration) return row
    const record = byId.get(id)
    if (!record) return row
    const mapped = mapAdderLibraryRecordToQuoteRow(record, defaultTaxRate)
    return { ...mapped, quantity: row.quantity ?? mapped.quantity }
  })
}

/** Whether library incentive rows need title/value hydration from the incentives resource. */
export const quoteIncentiveRowsNeedLibraryHydration = (rows: QuoteIncentiveRow[]): boolean =>
  rows.some((row) => {
    if (typeof row.incentive_id !== 'number' || row.incentive_id <= 0) return false
    return !row.title.trim() || (row.settings.value == null && row.inc_tax == null)
  })

export const hydrateLibraryIncentiveTitles = (
  rows: QuoteIncentiveRow[],
  libraryRecords: IncentiveLibraryRecord[]
): QuoteIncentiveRow[] => {
  if (!quoteIncentiveRowsNeedLibraryHydration(rows)) return rows
  const byId = new Map(libraryRecords.map((r) => [r.id, r]))
  return rows.map((row) => {
    const id = row.incentive_id
    if (typeof id !== 'number' || id <= 0) return row
    const record = byId.get(id)
    if (!record) return row
    if (record.incentive_paid_to != null && record.incentive_paid_to !== INCENTIVE_PAID_TO_INSTALLER) {
      return row
    }
    const needsTitle = !row.title.trim()
    const needsValue = row.settings.value == null && row.inc_tax == null
    if (!needsTitle && !needsValue) return row

    const mapped = mapIncentiveLibraryRecordToQuoteRow(record)
    return {
      ...row,
      title: needsTitle ? mapped.title : row.title,
      incentive_type: mapped.incentive_type,
      inc_tax: needsValue ? mapped.inc_tax : row.inc_tax,
      settings: {
        ...row.settings,
        value: needsValue ? mapped.settings.value : row.settings.value,
        price_based_incentive_type:
          mapped.settings.price_based_incentive_type ?? row.settings.price_based_incentive_type,
      },
    }
  })
}

export const isPriceBasedIncentiveType = (incentiveType: number): boolean =>
  incentiveType === PRICE_BASED_INCENTIVE
